import { createColumnHelper } from '@tanstack/react-table'
import { IInvestmentStatus } from '~/@api/generated-api-types-account/Api'
import { centsToEur } from '~/@lib/utils/money.utils'
import { formatDate } from '~/@lib/utils/date.utils'
import { formatAmount } from '~/@lib/utils/number.utils'
import { Text } from '~/@mid_ui/typography/Text'
import { TextTableHead } from '~/@mid_ui/typography/TextTableHead'
import { TextTableCell } from '~/@mid_ui/typography/TextTableCell'
import { StageLabel } from '~/@mid_ui/typography/StageLabel'
import { ProjectCellContainer } from '~/@pages_investing/projects/_projects_ui/project-cell-container/ProjectCellContainer'
import { projectTooltipByType } from '~/@pages_investing/projects/_projects_ui/project-info-tooltips/projectTooltipByType'
import { ProjectVipBadge } from '~/@pages_investing/projects/_projects_ui/project-vip-badge/ProjectVipBadge'
import { getProjectLoanPurposeLabel } from '~/@pages_investing/projects/_projects_ui/project-loan-purpose/labelByProjectLoanPurpose'
import type { ProjectsItem } from '~/@pages_investing/projects/_project_types/project.types'
import { ProjectPreviewImageAndPopup } from '~/@pages_investing/projects/_projects_ui/project-preview-and-label/ProjectPreviewImageAndPopup'
import {
  isInvestmentDelayedOrExtended,
  minMaxFilter,
} from '../_investments_filtering/filtering.utils'
import { InvestmentYield } from '../investments_ui/investment-yield/InvestmentYield'
import type { Investment } from '../_investment_types/investment.types'
import { investmentTooltips } from './investmentTooltips'
import { getInvestmentStatusLabel } from '../investments_ui/investment-status/labelByInvestmentStatus'
import { WithdrawInvestmentTooltip } from '../investments_ui/withdraw-investment-tooltip/WithdrawInvestmentTooltip'

const columnHelper = createColumnHelper<Investment>()

const columns = [
  columnHelper.accessor('project.loanPurpose', {
    id: 'project.loanPurpose',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>Účel úveru</TextTableHead>
        {projectTooltipByType.loanPurpose}
      </ProjectCellContainer>
    ),
    cell: ({ row }) => {
      const { project } = row.original
      return (
        <div className="flex gap-1">
          <div className="flex h-full w-[50px] items-center justify-center">
            {/* BE portfolio projekcia blurTags/blurTagNames NEposiela (1:1 Meteor
                `loadInvestments` projectFields) — badge sa tu nikdy nerenderoval. */}
            <ProjectVipBadge blurTagNames={[]} blurTags={[]} />
          </div>
          <div className="flex flex-col items-start justify-center font-semibold">
            <Text>
              {project.loanPurpose
                ? getProjectLoanPurposeLabel(project.loanPurpose)
                : null}
            </Text>
            <div className="text-accent underline text-sm">
              {project.businessId}
            </div>
          </div>
        </div>
      )
    },
  }),
  columnHelper.accessor('project.collateralInfo.collateralSubcategory', {
    id: 'project.collateralInfo.collateralSubcategory',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>Zaistené</TextTableHead>
        {projectTooltipByType.category}
      </ProjectCellContainer>
    ),
    cell: ({ row: { original: investment } }) => (
      <TextTableCell className="flex flex-row gap-2.5">
        <ProjectPreviewImageAndPopup
          withPopup={false}
          project={investment.project as unknown as ProjectsItem}
        />
      </TextTableCell>
    ),
  }),
  columnHelper.accessor('totalYieldPerAnnum', {
    id: 'totalYieldPerAnnum',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Priemerná <br /> úroková sadzba
        </TextTableHead>
        {projectTooltipByType.yield}
      </ProjectCellContainer>
    ),
    cell: ({ row }) => <InvestmentYield investment={row.original} />,
  }),
  columnHelper.accessor('value', {
    id: 'value',
    header: () => (
      <ProjectCellContainer>
        <div className="flex flex-col items-center justify-start gap-2">
          <TextTableHead>
            Moja <br /> investícia
          </TextTableHead>
          <StageLabel className="mb-1">Tranža</StageLabel>
        </div>
        {projectTooltipByType.investedAmount}
      </ProjectCellContainer>
    ),
    cell: ({ row }) => {
      const {
        project,
        value: investmentValue,
        status,
        prereservationData,
      } = row.original

      const prereservationValue = [
        prereservationData?.plan.disponibleFundsFixed || 0,
        prereservationData?.plan.futureRevenueFixed || 0,
      ].reduce((acc, value) => acc + value, 0)

      const value =
        status === IInvestmentStatus.Prereserved
          ? centsToEur(prereservationValue)
          : investmentValue

      return (
        <div className="flex items-center gap-2">
          <TextTableCell className="flex flex-row items-center gap-2">
            <span>{formatAmount(value, { maximumFractionDigits: 2 })}</span>
            {project.stageNumber && (
              <StageLabel>{project.stageNumber}.</StageLabel>
            )}
          </TextTableCell>
        </div>
      )
    },
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('currentCompoundedRevenueValue', {
    enableSorting: false,
    header: () => {
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1

      return (
        <ProjectCellContainer>
          <div className="flex items-center justify-start gap-2">
            <TextTableHead>
              Pripísaný výnos
              <br />k 1.{currentMonth}.{currentYear}
            </TextTableHead>
          </div>
          {projectTooltipByType.yieldCompoundingRevenue}
        </ProjectCellContainer>
      )
    },
    cell: ({ row }) => {
      const { currentCompoundedRevenueValue } = row.original
      if (currentCompoundedRevenueValue == null) return <></>

      return (
        <div className="flex items-center gap-2">
          <TextTableCell className="flex flex-row items-center gap-2">
            <span>
              {formatAmount(currentCompoundedRevenueValue, {
                maximumFractionDigits: 2,
              })}
            </span>
          </TextTableCell>
        </div>
      )
    },
  }),
  columnHelper.accessor('totalPrincipalWithYieldValue', {
    id: 'totalPrincipalWithYieldValue',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Investícia <br /> výnosom
        </TextTableHead>
        {investmentTooltips.total}
      </ProjectCellContainer>
    ),
    cell: ({ row }) => (
      <div className="flex gap-2">
        <TextTableCell>
          {formatAmount(row.original.totalPrincipalWithYieldValue, {
            maximumFractionDigits: 2,
          })}
        </TextTableCell>
      </div>
    ),
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('nettoBonusValue', {
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          &nbsp; <br /> Bonus
        </TextTableHead>
      </ProjectCellContainer>
    ),
    cell: ({ getValue }) => {
      const bonus = getValue()

      return (
        <div className="flex gap-2">
          <TextTableCell>
            <span>
              {formatAmount(bonus ?? 0, { maximumFractionDigits: 2 })}
            </span>
          </TextTableCell>
        </div>
      )
    },
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Dátum <br /> investície
        </TextTableHead>
        {investmentTooltips.createdAt}
      </ProjectCellContainer>
    ),
    cell: ({ row }) => (
      <TextTableCell className="flex items-center gap-3">
        {formatDate(row.original.createdAt)}
        <WithdrawInvestmentTooltip investment={row.original} />
      </TextTableCell>
    ),
  }),
  columnHelper.accessor('latestPaymentDate', {
    id: 'latestPaymentDate',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Dátum <br /> splatenia
        </TextTableHead>
        {projectTooltipByType.payoutDate}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const value = info.getValue()
      return (
        <TextTableCell>{value ? `${formatDate(value)}` : '-'}</TextTableCell>
      )
    },
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Stav <br /> investície
        </TextTableHead>
        {investmentTooltips.status}
      </ProjectCellContainer>
    ),
    cell: ({ row }) => {
      const { status, extendPeriod, latestPaymentDate } = row.original

      const isDelayed =
        (latestPaymentDate &&
          new Date(latestPaymentDate).getTime() < new Date().getTime()) ||
        false
      const isExtended = extendPeriod > 0

      let finalInvestmentStatus = status
      if (status === IInvestmentStatus.Invested) {
        if (isDelayed) {
          finalInvestmentStatus = IInvestmentStatus.Delayed
        } else if (isExtended) {
          finalInvestmentStatus = IInvestmentStatus.Extended
        }
      }
      const isDelayedOrExtended = isInvestmentDelayedOrExtended(row.original)

      return (
        <TextTableCell className="flex items-center gap-2">
          <Text>{getInvestmentStatusLabel(finalInvestmentStatus)}</Text>
          {isDelayedOrExtended && <>{investmentTooltips.investmentExtended}</>}
        </TextTableCell>
      )
    },
  }),
]

export { columns }
